// SDK Bridge Protocol v1 的 widget 侧实现。权威契约见 docs/SDK_BRIDGE_PROTOCOL.md。
// 无任何依赖，可被 node --test 直接加载；所有 window 访问都在函数内并做存在性防御。

var PROTOCOL_VERSION = 1;
var CALL_TIMEOUT_MS = 3000;

function detectTransport() {
    if (typeof window === 'undefined') return null;
    try {
        if (window.__laneChatAndroid && typeof window.__laneChatAndroid.postMessage === 'function') {
            return { platform: 'android', send: function (s) { window.__laneChatAndroid.postMessage(s); } };
        }
    } catch (e) { /* cross-origin / sealed object guards */ }
    try {
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.laneChat) {
            return { platform: 'ios', send: function (s) { window.webkit.messageHandlers.laneChat.postMessage(s); } };
        }
    } catch (e) { }
    try {
        if (window.__laneChatFlutter && typeof window.__laneChatFlutter.postMessage === 'function') {
            return { platform: 'flutter', send: function (s) { window.__laneChatFlutter.postMessage(s); } };
        }
    } catch (e) { }
    return null;
}

var bridge = {
    _transport: null,
    _helloAcked: false,
    _capabilities: [],
    _settings: {},
    _pending: {},
    _seq: 0,
    _nativeListeners: {},

    init: function (widgetVersion) {
        this._transport = detectTransport();
        if (!this._transport) return false;
        var self = this;
        window.__laneChatBridge = { dispatch: function (raw) { self._onDispatch(raw); } };
        this._send({ v: 1, type: 'event', name: 'hello', payload: { widgetVersion: String(widgetVersion || ''), protocol: PROTOCOL_VERSION } });
        return true;
    },

    isNative: function () { return !!this._transport; },
    isActive: function () { return !!this._transport && this._helloAcked; },
    supports: function (name) { return this._capabilities.indexOf(name) !== -1; },
    platform: function () { return this._transport ? this._transport.platform : null; },
    settings: function () { return this._settings; },

    event: function (name, payload) {
        if (!this._transport) return;
        this._send({ v: 1, type: 'event', name: name, payload: payload || {} });
    },

    call: function (name, payload) {
        var self = this;
        if (!this._transport) {
            return Promise.resolve({ ok: false, unsupported: true });
        }
        return new Promise(function (resolve) {
            var id = 'c' + (++self._seq);
            var timer = setTimeout(function () {
                delete self._pending[id];
                resolve({ ok: false, timeout: true });
            }, CALL_TIMEOUT_MS);
            self._pending[id] = function (payload) {
                clearTimeout(timer);
                resolve(payload || { ok: false });
            };
            self._send({ v: 1, type: 'call', name: name, id: id, payload: payload || {} });
        });
    },

    onNativeEvent: function (name, cb) {
        if (typeof cb !== 'function') return function () {};
        if (!this._nativeListeners[name]) this._nativeListeners[name] = [];
        this._nativeListeners[name].push(cb);
        var self = this;
        return function () {
            var arr = self._nativeListeners[name];
            if (!arr) return;
            var i = arr.indexOf(cb);
            if (i !== -1) arr.splice(i, 1);
        };
    },

    _send: function (envelope) {
        try {
            this._transport.send(JSON.stringify(envelope));
        } catch (e) {
            if (typeof console !== 'undefined') console.warn('[LaneChat bridge] send failed:', e && e.message);
        }
    },

    _onDispatch: function (raw) {
        var msg;
        try { msg = JSON.parse(raw); } catch (e) { return; }
        if (!msg || msg.v !== 1) return;
        if (msg.type === 'result') {
            var pending = this._pending[msg.id];
            if (pending) { delete this._pending[msg.id]; pending(msg.payload); }
            return;
        }
        if (msg.type === 'event') {
            if (msg.name === 'helloAck') {
                var p = msg.payload || {};
                this._helloAcked = true;
                this._capabilities = Array.isArray(p.capabilities) ? p.capabilities : [];
                this._settings = p.settings || {};
            }
            var arr = this._nativeListeners[msg.name];
            if (arr) {
                arr.slice().forEach(function (cb) {
                    try { cb(msg.payload || {}); } catch (e) { /* listener errors must not break dispatch */ }
                });
            }
            return;
        }
        // 未知 type：按协议静默忽略
    },

    _resetForTests: function () {
        this._transport = null;
        this._helloAcked = false;
        this._capabilities = [];
        this._settings = {};
        this._pending = {};
        this._seq = 0;
        this._nativeListeners = {};
    }
};

/**
 * 是否运行在原生容器（Android/iOS/Flutter WebView 宿主）里。纯探测，不依赖
 * bridge.init() 跑过——直接复用 detectTransport() 而不是读 bridge._transport，
 * 因为 init() 要等 widget-index.js 的 injectChat().then() 才调用（组件首次渲染
 * 之后一拍），close 按钮的可见性判断等不了那一拍，得在渲染期随时可查。
 *
 * 用途：native 全屏容器没有"折叠回浮动按钮"这条退路，widget 侧的 inline 模式
 * close 按钮抑制逻辑（isInline 时不渲染 ✕）对 native 不适用——必须无视那条判断，
 * 永远显示关闭按钮，点击后把 close 事件交给原生宿主处理（见 widget.js
 * onClickClose 的 hasNativeTransport 分支、ChatCloseSynthesis.kt 的文档注释）。
 */
export function hasNativeTransport() {
    return !!detectTransport();
}

/**
 * native → widget `navigate` 事件 payload 的归一化（SDK Bridge Protocol v1，
 * 见 docs/SDK_BRIDGE_PROTOCOL.md）：通知栏点开 / App 内跳转时 WebView 已经开着
 * 的热切换。纯函数，不碰 widget 实例——真正调用 Widget.switchToChat/
 * switchToHome 的副作用留给 widget-index.js 的 onNativeEvent('navigate', ...)
 * 订阅者，和 URL 参数（widget.html ?view=chat&chat_id=）走同一条内部导航路径。
 *
 *   {view:'chat'}              → {view:'chat', chatId:null}
 *   {view:'chat', chatId:123}  → {view:'chat', chatId:123}
 *   {view:'chat', chatId:-1}   → {view:'chat', chatId:null}（非法值当未给）
 *   {view:'home'}              → {view:'home'}
 *   {view:'unknown'} / 缺 view → null（调用方按"忽略 + debug log"处理）
 */
export function resolveNavigateTarget(payload) {
    var view = payload && payload.view;
    if (view === 'chat') {
        var chatId = payload && payload.chatId;
        var validChatId = (typeof chatId === 'number' && Number.isFinite(chatId) && chatId > 0 && Math.trunc(chatId) === chatId)
            ? chatId : null;
        return { view: 'chat', chatId: validChatId };
    }
    if (view === 'home') return { view: 'home' };
    return null;
}

/**
 * 把 native 的 `deeplink` 事件载荷（`{url}`）翻成 `navigate` 的形状
 * （`{view, chatId}`），再交给同一个 resolveNavigateTarget 裁决。
 *
 * 【为什么需要这层翻译】三端 native 都在发 `deeplink({url})`
 * （ChatWebViewController.kt / LaneChatWebView.swift / macOS 同名方法），
 * 而 widget 只订阅了 `navigate({view, chatId})` —— 且 `navigate` **三端都不发**。
 * 两半各自写完、各自有测试，中间那一跳没人接：WebView 已经开着时的热切换
 * （通知栏点开、App 内跳转）在三端全部静默失效，不报错不打日志。
 *
 * 翻译放在 widget 侧而不是改三端 native，是因为 native 是**已发布的固定版本**：
 * 改 widget 一处即可覆盖所有已在野的 SDK 版本，改 native 只能覆盖将来升级的那些。
 *
 * URL 形状与 widget.html 的 `?view=chat&chat_id=` 完全一致（native 拼的就是它），
 * 不发明第二套参数名。非我们的 URL、或参数缺失 → 返回 null，由调用方忽略。
 */
// 仅用于把相对 URL 解析成可读 searchParams；origin 不参与任何判断。
var DEEP_LINK_PARSE_BASE = 'https://lane.invalid/';

export function navigateTargetFromDeepLink(payload) {
    var raw = payload && payload.url;
    if (typeof raw !== 'string' || !raw) return null;
    var params;
    try {
        // 相对/绝对都吃。base 用一个固定的哑值而不是 window.location：这里只读
        // searchParams，origin 从头到尾没用上；而依赖 window 会让"裸 query"
        // （iOS handlePush 产出的 `?view=chat&chat_id=42` 正是这种）在任何没有
        // location 的环境里直接抛 —— 包括测试环境，于是这条路就测不到。
        params = new URL(raw, DEEP_LINK_PARSE_BASE).searchParams;
    } catch (e) {
        return null;
    }
    var view = (params.get('view') || '').trim().toLowerCase();
    if (!view) return null;
    var rawChatId = params.get('chat_id');
    // chat_id 在 URL 上永远是字符串，而 resolveNavigateTarget 只认正整数 number
    // （它同时服务于 native 直接发来的结构化载荷）——在这里转，别放宽它的校验。
    var chatId = null;
    if (rawChatId !== null && /^\d+$/.test(rawChatId.trim())) {
        var n = parseInt(rawChatId.trim(), 10);
        if (n > 0) chatId = n;
    }
    return resolveNavigateTarget({ view: view, chatId: chatId });
}

export function openLink(url, opts) {
    opts = opts || {};
    var fallback = function () {
        if (typeof window !== 'undefined' && window.open) {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };
    if (bridge.isActive() && bridge.supports('openUrl')) {
        bridge.call('openUrl', {
            url: url,
            source: opts.source || 'message',
            disposition: opts.disposition
        }).then(function (res) {
            // 只有 native 明确说"不认识这个 call"才兜底。
            // timeout 不兜底：晚到的 window.open 已脱离用户手势，多半被拦截还可能双开。
            if (res && res.unsupported) fallback();
        });
        return;
    }
    fallback();
}

export default bridge;
